import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Provider Definitions ──────────────────────────────────
export const AI_PROVIDERS = [
  {
    id: 'anthropic',
    label: 'Anthropic (Claude)',
    icon: '🤖',
    keyPlaceholder: 'sk-ant-...',
    keyLabel: 'API Key',
    keyHint: 'Get key at console.anthropic.com/keys',
    keyHintUrl: 'https://console.anthropic.com/keys',
    baseUrl: 'https://api.anthropic.com/v1/messages',
    baseUrlEditable: false,
    models: [
      { id: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Recommended)' },
      { id: 'claude-opus-4-20250514',   label: 'Claude Opus 4 (Most Capable)' },
      { id: 'claude-haiku-4-5-20251001',label: 'Claude Haiku 4.5 (Fastest)' },
    ],
    defaultModel: 'claude-sonnet-4-20250514',
    extraHeaders: {},
    docsUrl: 'https://docs.anthropic.com/en/api/getting-started',
  },
  {
    id: 'google_ai_studio',
    label: 'Google AI Studio (Gemini)',
    icon: '✦',
    keyPlaceholder: 'AIza...',
    keyLabel: 'API Key',
    keyHint: 'Get key at aistudio.google.com/apikey',
    keyHintUrl: 'https://aistudio.google.com/apikey',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai/chat/completions',
    baseUrlEditable: false,
    models: [
      { id: 'gemini-2.0-flash',      label: 'Gemini 2.0 Flash (Fast)' },
      { id: 'gemini-2.5-pro',        label: 'Gemini 2.5 Pro (Most Capable)' },
      { id: 'gemini-1.5-flash',      label: 'Gemini 1.5 Flash' },
      { id: 'gemini-1.5-pro',        label: 'Gemini 1.5 Pro' },
    ],
    defaultModel: 'gemini-2.0-flash',
    extraHeaders: {},
    docsUrl: 'https://ai.google.dev/gemini-api/docs/openai',
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    icon: '⚡',
    keyPlaceholder: 'sk-or-v1-...',
    keyLabel: 'API Key',
    keyHint: 'Get key at openrouter.ai/keys',
    keyHintUrl: 'https://openrouter.ai/keys',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    baseUrlEditable: false,
    models: [
      { id: 'google/gemini-2.0-flash-001',            label: 'Gemini 2.0 Flash' },
      { id: 'anthropic/claude-sonnet-4-5',             label: 'Claude Sonnet 4.5' },
      { id: 'meta-llama/llama-3.3-70b-instruct',      label: 'Llama 3.3 70B' },
      { id: 'mistralai/mistral-small-3.2-24b-instruct', label: 'Mistral Small 3.2' },
      { id: 'deepseek/deepseek-r1-0528',              label: 'DeepSeek R1' },
      { id: 'openai/gpt-4o-mini',                     label: 'GPT-4o Mini' },
    ],
    defaultModel: 'google/gemini-2.0-flash-001',
    extraHeaders: {},
    docsUrl: 'https://openrouter.ai/docs/quick-start',
  },
  {
    id: 'ollama',
    label: 'Ollama (Local)',
    icon: '🦙',
    keyPlaceholder: '(leave empty)',
    keyLabel: 'API Key (optional)',
    keyHint: 'No key needed for local Ollama',
    keyHintUrl: null,
    baseUrl: 'http://localhost:11434/v1/chat/completions',
    baseUrlEditable: true,
    models: [
      { id: 'llama3.2',   label: 'Llama 3.2 (3B)' },
      { id: 'llama3.1',   label: 'Llama 3.1 (8B)' },
      { id: 'mistral',    label: 'Mistral 7B' },
      { id: 'gemma3',     label: 'Gemma 3' },
      { id: 'qwen3',      label: 'Qwen3' },
      { id: 'phi4',       label: 'Phi-4' },
      { id: 'custom',     label: 'Custom model name...' },
    ],
    defaultModel: 'llama3.2',
    extraHeaders: {},
    docsUrl: 'https://ollama.com/blog/openai-compatibility',
  },
  {
    id: 'nginx_protected',
    label: 'Nginx / Custom (Header Auth)',
    icon: '🔒',
    keyPlaceholder: 'your-secret-token',
    keyLabel: 'Auth Header Value',
    keyHint: 'Value sent in your custom auth header',
    keyHintUrl: null,
    baseUrl: 'https://your-proxy.example.com/v1/chat/completions',
    baseUrlEditable: true,
    models: [
      { id: 'gpt-4o',            label: 'GPT-4o (via proxy)' },
      { id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (via proxy)' },
      { id: 'custom',            label: 'Custom model name...' },
    ],
    defaultModel: 'gpt-4o',
    extraHeaders: { 'X-Auth-Token': '' }, // user can edit header name
    docsUrl: null,
  },
  {
    id: 'openai_compatible',
    label: 'OpenAI Compatible (Custom)',
    icon: '🔧',
    keyPlaceholder: 'sk-...',
    keyLabel: 'API Key',
    keyHint: 'Any OpenAI-compatible endpoint',
    keyHintUrl: null,
    baseUrl: 'https://api.openai.com/v1/chat/completions',
    baseUrlEditable: true,
    models: [
      { id: 'gpt-4o',       label: 'GPT-4o' },
      { id: 'gpt-4o-mini',  label: 'GPT-4o Mini' },
      { id: 'gpt-4-turbo',  label: 'GPT-4 Turbo' },
      { id: 'custom',       label: 'Custom model name...' },
    ],
    defaultModel: 'gpt-4o',
    extraHeaders: {},
    docsUrl: 'https://platform.openai.com/docs/api-reference/chat',
  },
];

// ── Load / Save helpers ───────────────────────────────────
export function loadAiConfig() {
  try {
    const raw = localStorage.getItem('wa_ai_config');
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  // Legacy migration: old anthropic key
  const legacyKey   = localStorage.getItem('wa_anthropic_key');
  const legacyModel = localStorage.getItem('wa_anthropic_model');
  if (legacyKey) {
    return {
      provider: 'anthropic',
      apiKey: legacyKey,
      model: legacyModel || 'claude-sonnet-4-20250514',
      baseUrl: '',
      extraHeaders: {},
      customModel: '',
    };
  }
  return {
    provider: 'anthropic',
    apiKey: '',
    model: 'claude-sonnet-4-20250514',
    baseUrl: '',
    extraHeaders: {},
    customModel: '',
  };
}

export function saveAiConfig(cfg) {
  localStorage.setItem('wa_ai_config', JSON.stringify(cfg));
}

// ── Settings Page ─────────────────────────────────────────
export default function Settings() {
  const [cfg, setCfg]             = useState(loadAiConfig());
  const [saved, setSaved]         = useState(false);
  const [showKey, setShowKey]     = useState(false);
  const [customModel, setCustomModel] = useState('');
  const [headerName, setHeaderName]   = useState('X-Auth-Token');
  const [testStatus, setTestStatus]   = useState(null); // null | 'testing' | 'ok' | 'fail'
  const [testMsg, setTestMsg]         = useState('');

  const provider = AI_PROVIDERS.find(p => p.id === cfg.provider) || AI_PROVIDERS[0];

  useEffect(() => {
    const loaded = loadAiConfig();
    setCfg(loaded);
    setCustomModel(loaded.customModel || '');
    const hn = loaded.extraHeaders ? Object.keys(loaded.extraHeaders)[0] : 'X-Auth-Token';
    setHeaderName(hn || 'X-Auth-Token');
  }, []);

  const update = (patch) => setCfg(prev => ({ ...prev, ...patch }));

  const selectProvider = (id) => {
    const p = AI_PROVIDERS.find(x => x.id === id);
    setCfg(prev => ({
      ...prev,
      provider: id,
      model: p.defaultModel,
      baseUrl: p.baseUrlEditable ? (prev.provider === id ? prev.baseUrl : p.baseUrl) : p.baseUrl,
      extraHeaders: { ...p.extraHeaders },
      customModel: '',
    }));
    setCustomModel('');
    setTestStatus(null);
  };

  const save = () => {
    const finalCfg = {
      ...cfg,
      customModel,
      extraHeaders: provider.id === 'nginx_protected'
        ? { [headerName]: cfg.apiKey }
        : cfg.extraHeaders,
    };
    saveAiConfig(finalCfg);
    setSaved(true);
    setTestStatus(null);
    setTimeout(() => setSaved(false), 2000);
  };

  const testConnection = async () => {
    setTestStatus('testing');
    setTestMsg('');
    try {
      const finalModel = cfg.model === 'custom' ? customModel : cfg.model;
      const result = await window.__testAiProvider?.(cfg.provider, cfg.apiKey, finalModel, cfg.baseUrl, cfg.extraHeaders, headerName);
      if (result?.ok) {
        setTestStatus('ok');
        setTestMsg(result.msg || 'Connection successful!');
      } else {
        setTestStatus('fail');
        setTestMsg(result?.msg || 'Connection failed');
      }
    } catch (e) {
      setTestStatus('fail');
      setTestMsg(e.message || 'Unknown error');
    }
  };

  const isCustomModel = cfg.model === 'custom';

  return (
    <div style={{ padding: '20px 16px', maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 24 }}>
        SETTINGS
      </h2>

      {/* AI Configuration Card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.05em', marginBottom: 16 }}>
          // AI TEMPLATE GENERATION
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.6 }}>
          Choose your AI provider for template generation. The key is stored only in your browser's local storage.
        </p>

        {/* Provider selector */}
        <div style={{ marginBottom: 20 }}>
          <label className="label">AI Provider</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
            {AI_PROVIDERS.map(p => (
              <button
                key={p.id}
                onClick={() => selectProvider(p.id)}
                style={{
                  background: cfg.provider === p.id ? 'rgba(79,209,197,0.12)' : 'var(--bg3)',
                  border: `1px solid ${cfg.provider === p.id ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--r-sm)',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: cfg.provider === p.id ? 'var(--accent)' : 'var(--text2)',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 16 }}>{p.icon}</span>
                <span style={{ fontWeight: cfg.provider === p.id ? 600 : 400 }}>{p.label}</span>
              </button>
            ))}
          </div>
          {provider.docsUrl && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
              📖{' '}
              <a href={provider.docsUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)' }}>
                View {provider.label} API Docs
              </a>
            </div>
          )}
        </div>

        {/* API Key */}
        <div style={{ marginBottom: 16 }}>
          <label className="label">{provider.keyLabel}</label>

          {/* For nginx: show header name field first */}
          {provider.id === 'nginx_protected' && (
            <div style={{ marginBottom: 8 }}>
              <input
                type="text"
                value={headerName}
                onChange={e => setHeaderName(e.target.value)}
                placeholder="Header name, e.g. X-Auth-Token"
                style={{ marginBottom: 4 }}
              />
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Custom header name sent with each request</div>
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <input
              type={showKey ? 'text' : 'password'}
              value={cfg.apiKey}
              onChange={e => update({ apiKey: e.target.value })}
              placeholder={provider.keyPlaceholder}
              style={{ paddingRight: 80 }}
            />
            <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
              <button onClick={() => setShowKey(s => !s)} className="btn-ghost" style={{ fontSize: 12, padding: '4px 8px' }}>
                {showKey ? '🙈' : '👁'}
              </button>
              {cfg.apiKey && (
                <button onClick={() => update({ apiKey: '' })} className="btn-ghost" style={{ fontSize: 12, padding: '4px 8px', color: 'var(--danger)' }}>✕</button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
            {provider.keyHint}{' '}
            {provider.keyHintUrl && (
              <a href={provider.keyHintUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)' }}>
                {provider.keyHintUrl}
              </a>
            )}
          </div>
        </div>

        {/* Base URL (editable providers) */}
        {provider.baseUrlEditable && (
          <div style={{ marginBottom: 16 }}>
            <label className="label">Endpoint URL</label>
            <input
              type="text"
              value={cfg.baseUrl || provider.baseUrl}
              onChange={e => update({ baseUrl: e.target.value })}
              placeholder={provider.baseUrl}
            />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              Any OpenAI-compatible /v1/chat/completions endpoint
            </div>
          </div>
        )}

        {/* Extra headers (for nginx/custom) */}
        {provider.id === 'openai_compatible' && (
          <div style={{ marginBottom: 16 }}>
            <label className="label">Extra Headers <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(optional, JSON)</span></label>
            <input
              type="text"
              value={cfg.rawExtraHeaders || '{}'}
              onChange={e => {
                update({ rawExtraHeaders: e.target.value });
                try { update({ extraHeaders: JSON.parse(e.target.value) }); } catch (_) {}
              }}
              placeholder='{"X-Custom-Header": "value"}'
            />
          </div>
        )}

        {/* Model */}
        <div style={{ marginBottom: 20 }}>
          <label className="label">Model</label>
          <select value={cfg.model} onChange={e => update({ model: e.target.value })}>
            {provider.models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {isCustomModel && (
            <input
              type="text"
              value={customModel}
              onChange={e => setCustomModel(e.target.value)}
              placeholder="Enter exact model name, e.g. llama3.2:latest"
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        {/* Test connection status */}
        {testStatus && (
          <div style={{
            padding: '10px 14px',
            borderRadius: 'var(--r-sm)',
            marginBottom: 16,
            fontSize: 12,
            background: testStatus === 'ok' ? 'rgba(79,209,197,0.1)' : testStatus === 'testing' ? 'var(--bg3)' : 'rgba(255,100,100,0.1)',
            border: `1px solid ${testStatus === 'ok' ? 'var(--accent)' : testStatus === 'testing' ? 'var(--border)' : 'var(--danger)'}`,
            color: testStatus === 'ok' ? 'var(--accent)' : testStatus === 'testing' ? 'var(--text2)' : 'var(--danger)',
          }}>
            {testStatus === 'testing' && '⏳ Testing connection...'}
            {testStatus === 'ok' && `✅ ${testMsg}`}
            {testStatus === 'fail' && `❌ ${testMsg}`}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={save} style={{ minWidth: 120 }}>
            {saved ? '✅ Saved!' : 'Save Settings'}
          </button>
          {cfg.apiKey && provider.id !== 'ollama' && (
            <button className="btn-ghost" onClick={testConnection} disabled={testStatus === 'testing'} style={{ fontSize: 12 }}>
              🔌 Test Connection
            </button>
          )}
          {cfg.apiKey && (
            <span style={{ fontSize: 12, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }}></span>
              {provider.label} configured
            </span>
          )}
        </div>
      </div>


      {/* ── Credential Files ── */}
      <CredentialFiles />

      {/* How it works */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '14px 16px', fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
        <div style={{ color: 'var(--text2)', fontWeight: 600, marginBottom: 8 }}>How AI Generation Works</div>
        <div>• Describe your template in plain language (e.g. "order confirmation with OTP")</div>
        <div>• AI will suggest the right category, body text, header, footer &amp; buttons</div>
        <div>• You can edit the generated template before submitting to Meta</div>
        <div style={{ marginTop: 8 }}>
          <span style={{ color: 'var(--accent)' }}>Supported:</span>{' '}
          <span>Anthropic, Google AI Studio, OpenRouter, Ollama (local), Nginx proxy, any OpenAI-compatible endpoint</span>
        </div>
        <div style={{ marginTop: 6, color: 'var(--warn)' }}>
          ⚡ API calls go directly from your browser to the provider — your key never reaches our server
        </div>
      </div>
    </div>
  );

// ── Credential Files Component ────────────────────────────
function CredentialFiles() {
  const [files, setFiles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState('');
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null); // username
  const [fileData, setFileData] = useState({});   // {username: {...}}

  const flash = (m, isErr=false) => {
    isErr ? setError(m) : setMsg(m);
    setTimeout(() => { setMsg(''); setError(''); }, 4000);
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get('/businesses/cred-files');
      setFiles(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    try {
      await api.post('/businesses/cred-files/sync');
      flash('✅ Synced DB → file');
      await load();
    } catch (e) { flash(e.response?.data?.error || 'Sync failed', true); }
  };

  const restoreNow = async () => {
    if (!window.confirm('Restore credentials from your saved file into DB? Existing entries will be updated.')) return;
    try {
      const res = await api.post('/businesses/cred-files/restore');
      flash(`✅ Restored ${res.data.restored} business(es), ${res.data.numbers} number(s)`);
      await load();
    } catch (e) { flash(e.response?.data?.error || 'Restore failed', true); }
  };

  const downloadFile = (username) => {
    window.open(`/api/businesses/cred-files/${username}/download`, '_blank');
  };

  const viewFile = async (username) => {
    if (expanded === username) { setExpanded(null); return; }
    setExpanded(username);
    if (!fileData[username]) {
      try {
        const res = await api.get(`/businesses/cred-files/${username}`);
        setFileData(p => ({ ...p, [username]: res.data }));
      } catch (e) { flash('Could not read file', true); }
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.05em', marginBottom: 16 }}>
        // CREDENTIAL FILES
      </div>
      <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
        Credentials are auto-saved to <code style={{ color: 'var(--accent)', fontSize: 11 }}>credentials/{'{username}'}.json</code> on
        every add/edit/delete. On app restart, DB is automatically restored from these files.
      </p>

      {msg   && <div style={fs('ok')}>{msg}</div>}
      {error && <div style={fs('err')}>{error}</div>}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={syncNow} className="btn-ghost" style={{ fontSize: 12 }}>
          💾 Sync DB → File Now
        </button>
        <button onClick={restoreNow} className="btn-ghost" style={{ fontSize: 12 }}>
          🔄 Restore File → DB
        </button>
        <button onClick={() => downloadFile(files[0]?.username || 'admin')} className="btn-ghost" style={{ fontSize: 12 }}>
          📥 Download My File
        </button>
      </div>

      {/* File list */}
      {loading && <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}

      {!loading && files.length === 0 && (
        <div style={{ color: 'var(--text3)', fontSize: 12, padding: '12px 0' }}>
          No credential files found. Add a business to generate one.
        </div>
      )}

      {files.map(f => (
        <div key={f.username} style={{ marginBottom: 10, border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          {/* File header row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            background: 'var(--bg3)', cursor: 'pointer', flexWrap: 'wrap' }}
            onClick={() => viewFile(f.username)}>
            <span style={{ fontSize: 20 }}>📄</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{f.username}.json</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {f.biz_count} business{f.biz_count!==1?'es':''} · {f.num_count} number{f.num_count!==1?'s':''}
                {f.saved_at && <span> · Saved {new Date(f.saved_at).toLocaleString()}</span>}
                <span style={{ marginLeft: 8 }}>({Math.round((f.size_bytes||0)/1024*10)/10} KB)</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => downloadFile(f.username)} className="btn-ghost"
                style={{ fontSize: 11, padding: '3px 10px' }}>⬇️ Download</button>
            </div>
            <span style={{ color: 'var(--text3)', fontSize: 12 }}>{expanded === f.username ? '▲' : '▼'}</span>
          </div>

          {/* Expanded file content */}
          {expanded === f.username && fileData[f.username] && (
            <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', overflowX: 'auto' }}>
              {(fileData[f.username].businesses || []).map((biz, bi) => (
                <div key={bi} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>🏢</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{biz.label}</span>
                    <code style={{ fontSize: 10, color: 'var(--text3)', background: 'var(--bg3)',
                      padding: '2px 6px', borderRadius: 3 }}>{biz.business_account_id}</code>
                  </div>
                  <div style={{ marginLeft: 22 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                      Token: <code style={{ color: 'var(--text2)' }}>
                        {biz.access_token ? biz.access_token.slice(0,10)+'…'+biz.access_token.slice(-4) : '—'}
                      </code>
                    </div>
                    {(biz.numbers || []).map((n, ni) => (
                      <div key={ni} style={{ display: 'flex', alignItems: 'center', gap: 8,
                        padding: '4px 8px', background: 'var(--bg3)', borderRadius: 4, marginBottom: 3, fontSize: 12 }}>
                        <span>📱</span>
                        <span style={{ fontWeight: 500 }}>{n.label}</span>
                        {n.display_phone && <span style={{ color: 'var(--text3)' }}>{n.display_phone}</span>}
                        <code style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 'auto' }}>{n.phone_number_id}</code>
                      </div>
                    ))}
                    {(!biz.numbers || biz.numbers.length === 0) && (
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>No numbers</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.7 }}>
        💡 <strong>Auto-restore:</strong> If DB is reset or you move to a new server, just place the JSON files in
        <code style={{ color: 'var(--accent)', fontSize: 10, margin: '0 4px' }}>data/{'{APP_NAME}'}/credentials/</code>
        and restart — credentials will be restored automatically.
      </div>
    </div>
  );
}

function fs(t) {
  return { padding: '8px 12px', borderRadius: 'var(--r-sm)', fontSize: 12, marginBottom: 12,
    background: t==='ok' ? 'rgba(79,209,197,0.08)' : 'rgba(239,68,68,0.08)',
    border: `1px solid ${t==='ok' ? 'var(--accent)' : 'var(--danger)'}`,
    color: t==='ok' ? 'var(--accent)' : 'var(--danger)' };
}

}